import { useMutationApi, useQueryApi } from 'utils/api'
import { handleCommonErrors } from 'utils/api'
import { CustomError } from 'utils/customError'

import type { UseMutationOptions } from 'react-query'
import type { Method } from 'axios'
import type { CommonListResponse, CommonResponse } from 'types/api'
import type {
  AksiNyataCreateBody,
  AksiNyataOption,
  AksiNyataSession,
  MilUseQueryError,
  TopicDetails,
  Topic,
  TAPIGetAksiNyataRevisionResponse,
  TMilCommonErrorResponse,
  TAPIGetCertificateResponse,
  TAPIPostCertificateData,
  ResponseAksiNyataAppeal,
  APIPostAksiNyataAppeal,
  MilErrorResponse,
  TAPIGetTopicNewest,
  TAPIGetTopicRecommendation,
  OverallProgress,
  ResponseRerenderCertificatePDF,
  TAPIGetCertificateTopicDetailResponse,
  TAPIPostTopicCertificateData,
} from 'types/microlearning'

export class JenjangUnmatched extends CustomError {
  constructor(apiPath: string, originalError: any) {
    let title = 'Jenjang tidak sesuai'
    let description =
      'Topik yang anda akses tidak sesuai dengan jenjang akun anda.'
    super({ title, description, apiPath, originalError })
  }
}

export function useGetTopicList() {
  return useQueryApi<CommonListResponse<Topic>>(`/microlearnings/topics`, {
    errorHandlerFn: (status, path, error) => {
      handleCommonErrors(status ?? 500, path, error)
    },
  })
}

export function useGetTopicDetail(topicId: string) {
  return useQueryApi<CommonResponse<TopicDetails>>(
    `/microlearnings/topics/${topicId}`,
    {
      queryConfig: {
        enabled: !!topicId,
      },
      errorHandlerFn: (status, path, error, errorResponse) => {
        if (
          status === 400 &&
          !!errorResponse.data?.errors[0]?.code &&
          errorResponse.data?.errors[0]?.code === '08-118'
        ) {
          throw new JenjangUnmatched(path, error)
        } else {
          handleCommonErrors(status ?? 500, path, error)
        }
      },
    }
  )
}

export function useGetTopicAksiNyataOptions(topicId: string, enabled: boolean) {
  return useQueryApi<CommonListResponse<AksiNyataOption>, MilUseQueryError>(
    `/microlearnings/topics/${topicId}/aksi-nyata-options`,
    {
      queryConfig: {
        enabled: !!topicId && enabled,
      },
    }
  )
}

export function useGetTopicAksiNyataSession(
  topicId: string,
  enabled: boolean,
  data = {}
) {
  return useQueryApi<CommonResponse<AksiNyataSession>, MilUseQueryError>(
    `/microlearnings/topics/${topicId}/aksi-nyata`,
    {
      queryConfig: {
        enabled: !!topicId && enabled,
        useErrorBoundary: false,
      },
      axiosConfig: {
        data,
      },
    }
  )
}

export function usePostAksiNyata(
  topicId: string,
  method: Method,
  opts?: UseMutationOptions<
    CommonResponse<AksiNyataSession>,
    Error,
    AksiNyataCreateBody
  >
) {
  return useMutationApi<
    CommonResponse<AksiNyataSession>,
    Error,
    AksiNyataCreateBody
  >(`/microlearnings/topics/${topicId}/aksi-nyata`, {
    mutationConfig: opts,
    axiosConfig: {
      method,
    },
  })
}

export interface RelevanceTopics {
  topics: {
    id: string
    name: string
  }[]
}

export function useGetRelevanceTopic(topicId: string) {
  return useQueryApi<CommonResponse<RelevanceTopics>>(
    `/microlearnings/topics/${topicId}/relevances`,
    {
      queryConfig: {
        useErrorBoundary: false,
      },
    }
  )
}

export function useGetAksiNyataRevision(
  topicId: string | number,
  enabled: boolean
) {
  return useQueryApi<CommonResponse<TAPIGetAksiNyataRevisionResponse>>(
    `/microlearnings/topics/${topicId}/aksi-nyata/revision`,
    {
      queryConfig: {
        enabled: !!topicId && enabled,
        useErrorBoundary: false,
      },
    }
  )
}

export function usePostCertificate(
  topicId: string,
  method: Method,
  opts?: UseMutationOptions<
    void,
    TMilCommonErrorResponse,
    Pick<TAPIPostCertificateData, 'userProfile'>
  >
) {
  return useMutationApi<
    void,
    TMilCommonErrorResponse,
    Pick<TAPIPostCertificateData, 'userProfile'>
  >(`/microlearnings/topics/${topicId}/certificate`, {
    mutationConfig: opts,
    axiosConfig: {
      method,
    },
  })
}

type TUseGetCertificateParams = {
  enabled: boolean
  errorHandler?: (errorCode: string, error?: any) => void
  refetchInterval?: (
    data: CommonResponse<TAPIGetCertificateResponse, {}>
  ) => number | false
  topicId: string
}
export function useGetCertificate({
  enabled,
  errorHandler,
  refetchInterval,
  topicId,
}: TUseGetCertificateParams) {
  return useQueryApi<CommonResponse<TAPIGetCertificateResponse, {}>>(
    `/microlearnings/topics/${topicId}/certificate`,
    {
      axiosConfig: {
        method: 'GET',
      },
      queryConfig: {
        enabled: !!topicId && enabled,
        useErrorBoundary: false,
        refetchInterval,
      },
      errorHandlerFn: (_status, _path, error, errorResponse) => {
        const errorCode: string = errorResponse.data.errors[0].code
        errorHandler?.(errorCode, error)
      },
    }
  )
}

export function useTriggerGenerateCertificatePdf(
  topicId: string | number,
  opts?: UseMutationOptions<
    ResponseRerenderCertificatePDF,
    MilErrorResponse,
    unknown
  >
) {
  return useMutationApi<
    ResponseRerenderCertificatePDF,
    MilErrorResponse,
    unknown
  >(`/microlearnings/topics/${topicId}/certificate/rerender`, {
    mutationConfig: opts,
    axiosConfig: {
      method: 'POST',
    },
  })
}

export function usePostAksiNyataAppeal(
  topicId: string | number,
  opts?: UseMutationOptions<
    ResponseAksiNyataAppeal,
    MilErrorResponse,
    APIPostAksiNyataAppeal
  >
) {
  return useMutationApi<
    ResponseAksiNyataAppeal,
    MilErrorResponse,
    APIPostAksiNyataAppeal
  >(`/microlearnings/topics/${topicId}/aksi-nyata/appeal`, {
    mutationConfig: opts,
    axiosConfig: {
      method: 'POST',
    },
  })
}

export function useTopicNewest() {
  return useQueryApi<CommonListResponse<TAPIGetTopicNewest>, MilErrorResponse>(
    '/microlearnings/topic-recommendation',
    {
      queryConfig: {
        useErrorBoundary: false,
      },
    }
  )
}

export function useTopicRecommendation() {
  return useQueryApi<
    CommonListResponse<TAPIGetTopicRecommendation>,
    MilErrorResponse
  >('/microlearnings/section-page', {
    queryConfig: {
      useErrorBoundary: false,
    },
  })
}

export function useTopicProgress(enabled = true) {
  return useQueryApi<CommonListResponse<OverallProgress>, MilErrorResponse>(
    '/microlearnings/topics/progress',
    {
      queryConfig: {
        enabled,
        useErrorBoundary: false,
      },
    }
  )
}

export function useTopicCertificate({
  topicId,
  errorHandler,
}: {
  topicId: string | number
  errorHandler?: (errorCode: string, error?: any) => void
}) {
  return useQueryApi<CommonResponse<TAPIGetCertificateTopicDetailResponse>>(
    `/microlearnings/topics/${topicId}/surat-selesai-belajar`,
    {
      queryConfig: {
        enabled: !!topicId,
        useErrorBoundary: false,
      },
      errorHandlerFn: (_status, _path, error, errorResponse) => {
        const errorCode: string = errorResponse.data.errors[0].code
        errorHandler?.(errorCode, error)
      },
    }
  )
}

export function usePostTopicCertificate(
  topicId: string,
  opts?: UseMutationOptions<
    void,
    TMilCommonErrorResponse,
    Pick<TAPIPostTopicCertificateData, 'userProfile'>
  >
) {
  return useMutationApi<
    void,
    TMilCommonErrorResponse,
    Pick<TAPIPostTopicCertificateData, 'userProfile'>
  >(`/microlearnings/topics/${topicId}/surat-selesai-belajar`, {
    mutationConfig: opts,
    axiosConfig: {
      method: 'POST',
    },
  })
}
